import { useCallback, useState } from "react";

import { isAPIError } from "@/core/services";
import { readErrorDetails } from "@/core/utils";
import { PAPEL_OBRA_DUPLICATE_MESSAGE } from "./use-create-papel-obra";
import { updatePapelObra } from "./papeis-obra.gateway";
import type { UpdatePapelObraParams } from "./papeis-obra.types";

export const PAPEL_OBRA_UPDATE_FORBIDDEN_MESSAGE =
  "Você não tem permissão para editar papéis na obra.";
export const PAPEL_OBRA_UPDATE_ERROR_MESSAGE = "Não foi possível salvar o papel. Tente novamente.";
export const PAPEL_OBRA_NOT_FOUND_MESSAGE = "O papel não foi encontrado. A lista será atualizada.";

export type UpdatePapelObraResult =
  | { ok: true }
  | {
      ok: false;
      /** 403 — a tela mostra Toast "sem permissão". */
      forbidden: boolean;
      /** 404 — papel inexistente: a tela avisa e recarrega a lista. */
      notFound: boolean;
      /** Mensagem geral (falha inesperada) para Toast. */
      message: string | null;
      /** 409 / 400 em `nome` — vai no campo nome. */
      nameError: string | null;
      /** 400 em `janela.*` — vai no bloco da janela. */
      janelaError: string | null;
    };

const NO_FAILURE = {
  ok: false as const,
  forbidden: false,
  notFound: false,
  message: null,
  nameError: null,
  janelaError: null,
};

/** Nunca rejeita: devolve o resultado para a tela decidir onde mostrar o erro. */
export function useUpdatePapelObra() {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const update = useCallback(
    async (params: UpdatePapelObraParams): Promise<UpdatePapelObraResult> => {
      setIsSubmitting(true);

      try {
        await updatePapelObra(params);
        return { ok: true };
      } catch (error) {
        if (!isAPIError(error)) return { ...NO_FAILURE, message: PAPEL_OBRA_UPDATE_ERROR_MESSAGE };

        if (error.status === 403) return { ...NO_FAILURE, forbidden: true };
        if (error.status === 404) return { ...NO_FAILURE, notFound: true };

        if (error.status === 409) {
          return { ...NO_FAILURE, nameError: error.message || PAPEL_OBRA_DUPLICATE_MESSAGE };
        }

        if (error.status === 400) {
          const details = readErrorDetails(error.details);
          const janelaError =
            details["janela.saida"] ?? details["janela.entrada"] ?? details.janela ?? null;
          const nameError = details.nome ?? null;

          if (janelaError || nameError) return { ...NO_FAILURE, janelaError, nameError };
          return { ...NO_FAILURE, message: error.message || PAPEL_OBRA_UPDATE_ERROR_MESSAGE };
        }

        return { ...NO_FAILURE, message: PAPEL_OBRA_UPDATE_ERROR_MESSAGE };
      } finally {
        setIsSubmitting(false);
      }
    },
    [],
  );

  return { update, isSubmitting };
}
